// Proforma Invoices — BizPlus parity Phase 1.1.
//
// A proforma is a SalesOrder with documentType 'proforma' (see db-sales.js
// and useProforma.ts) — it shares its shape and backend with Quotations, but
// is its own numbered document (numbering-allocator.js, prefix 'PF') issued
// to a client for advance/reference purposes, NOT a tax invoice. Once
// confirmed it can be printed (two labelled ORIGINAL/DUPLICATE pages — see
// src/shared/documents/templates.js renderProforma) and raised as an
// Invoice Request the same way an accepted Quotation is.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  FileStack, Plus, Trash2, Wallet, Clock, Download, Eye, ExternalLink, X,
  Search, ArrowUp, ArrowDown, ArrowUpDown, CalendarRange, ChevronLeft, ChevronRight,
  CheckCircle2, XCircle,
} from 'lucide-react';
import { cn } from '@/shared/utils/utils';
import { Button } from '@/shared/components/ui/Button';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { StatCard, StatGrid } from '@/shared/components/ui/StatCard';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Badge } from '@/shared/components/ui/Badge';
import { LostReasonModal } from '@/features/deals/components/LostReasonModal';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { formatINR, formatDocTotal, docInrEquivalent } from '@/shared/utils/currency';
import { packSummary, ratePerBaseUnit, totalBaseQuantityLabel } from '@/shared/utils/ratePerBaseUnit';
import { abbreviatePackaging, derivePackagingType } from '@/shared/utils/packagingAbbreviation';
import { useProducts } from '@/features/catalog/hooks/useCatalogApi';
import { useDeals } from '@/features/deals/hooks/useDeals';
import { useUser } from '@/shared/hooks/useAuth';
import { ROUTES } from '@/routes/paths';
import { useUserOptions } from '@/shared/hooks/useUserOptions';
import {
  useProformaList,
  useProformaMutations,
  type Proforma,
  type ProformaLine,
  type ProformaSort,
} from '../hooks/useProforma';

/** Columns the backend allowlists for sorting (ORDER_SORTS, src/api-sales/orders.js). */
type ProformaSortColumn = 'customer' | 'amount' | 'date';
const SORT_ASC: Record<ProformaSortColumn, ProformaSort> = { customer: 'customer_asc', amount: 'amount_asc', date: 'date_asc' };
const SORT_DESC: Record<ProformaSortColumn, ProformaSort> = { customer: 'customer_desc', amount: 'amount_desc', date: 'date_desc' };

interface DraftLine extends ProformaLine {
  key: string;
  name: string;
  packSize?: string;
  qtyPerPack?: number;
  totalPackSize?: string;
  volumeLiters?: number;
}

const emptyLine = (): DraftLine => ({
  key: Math.random().toString(36).slice(2),
  productId: '',
  name: '',
  description: '',
  quantity: 1,
  unitPrice: 0,
  discount: 0,
  discountType: 'percent',
  cgstRate: 0,
  sgstRate: 0,
  igstRate: 0,
});

function errorText(err: unknown, fallback: string) {
  const e = err as { response?: { data?: { error?: string } } };
  return e?.response?.data?.error || fallback;
}

export function ProformaPage() {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const isFormOpen = creating || editingId !== null;
  const [previewProforma, setPreviewProforma] = useState<Proforma | null>(null);

  // Chromium's Print/Save-as-PDF dialog names the file after the TOP window's
  // title, not the iframe's — override the tab title while the preview is
  // open so Save-as-PDF picks up the real proforma number instead of
  // "RBM Oil — by Zynx" (index.html).
  useEffect(() => {
    if (!previewProforma) return;
    const original = document.title;
    document.title = previewProforma.orderNumber || previewProforma.documentNumber || 'Proforma';
    return () => { document.title = original; };
  }, [previewProforma]);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([emptyLine()]);

  const currentUser = useUser();
  const isCoordinator = currentUser?.role === 'sales_coordinator';
  const canSeeWholeDirectory = ['superadmin', 'admin', 'department_admin', 'sales_coordinator'].includes(currentUser?.role || '');
  const { data: userOptions = [] } = useUserOptions(canSeeWholeDirectory);
  const [createdBy, setCreatedBy] = useState(() => isCoordinator ? (currentUser?.id || '') : '');
  const [userSelectedFilter, setUserSelectedFilter] = useState(false);

  useEffect(() => {
    if (isCoordinator && !userSelectedFilter && !createdBy && currentUser?.id) {
      setCreatedBy(currentUser.id);
    }
  }, [isCoordinator, currentUser?.id, userSelectedFilter, createdBy]);
  const [sort, setSort] = useState<ProformaSort>('date_desc');
  const toggleSort = (column: ProformaSortColumn) => {
    setSort((cur) => (cur === SORT_DESC[column] ? SORT_ASC[column] : SORT_DESC[column]));
  };
  const sortIcon = (column: ProformaSortColumn) => {
    if (sort === SORT_ASC[column]) return <ArrowUp className="w-3 h-3" />;
    if (sort === SORT_DESC[column]) return <ArrowDown className="w-3 h-3" />;
    return <ArrowUpDown className="w-3 h-3 opacity-30" />;
  };

  // Search, status, date-range & pagination — brought up to the same server-
  // side parity Quotations already had (GET /api/sales-ops/orders already
  // supports all of this for every documentType).
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  // A comma-separated status list arrives from the dashboard's Open Proforma
  // tile (e.g. ?status=draft,proforma) so the tile's count and this list's
  // rows can never disagree — see src/shared/dashboard/kpi.js.
  const [statusFilter, setStatusFilter] = useState<string>(
    () => new URLSearchParams(window.location.search).get('status') || 'all',
  );
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, statusFilter, fromDate, toDate, pageSize, sort, createdBy]);

  const { data: pagedResult, isLoading, isFetching } = useProformaList({
    status: statusFilter,
    search: searchQuery,
    fromDate: fromDate || undefined,
    toDate: toDate || undefined,
    sort,
    createdBy: canSeeWholeDirectory ? createdBy || undefined : undefined,
    page,
    limit: pageSize,
  });
  const proformas = pagedResult?.data ?? [];
  const pagination = pagedResult?.pagination;
  const totalValue = pagedResult?.stats?.totalValue ?? 0;
  const openCount = pagedResult?.stats?.openCount ?? 0;
  const { data: productData } = useProducts();
  const { data: dealsData } = useDeals();
  const { create, update, markLost } = useProformaMutations();
  const [selectedForLostModal, setSelectedForLostModal] = useState<Proforma | null>(null);

  const renderStatusBadge = (p: Proforma) => {
    const isConfirmed = p.status === 'confirmed' || p.status === 'accepted';
    const isLost = p.status === 'cancelled' || !!p.lostReasonName;

    if (isConfirmed) {
      return (
        <Badge className="text-data-xs font-bold uppercase tracking-wider bg-success/10 text-success-text border-success/30 inline-flex items-center gap-1 w-fit">
          <CheckCircle2 className="w-3 h-3 text-success-text shrink-0" />
          Confirmed
        </Badge>
      );
    }

    if (isLost) {
      const reasonLower = (p.lostReasonName || p.lostReasonNote || '').toLowerCase();
      let label: string;
      if (reasonLower.includes('price')) label = 'Closed Lost: Price Issue';
      else if (reasonLower.includes('quality')) label = 'Closed Lost: Poor Quality';
      else if (p.lostReasonName) label = `Closed Lost: ${p.lostReasonName}`;
      else if (p.lostReasonNote) label = `Closed Lost: ${p.lostReasonNote}`;
      else label = 'Closed Lost: Other Issues';

      return (
        <Badge className="text-data-xs font-bold uppercase tracking-wider bg-danger/10 text-danger-text border-danger/30 inline-flex items-center gap-1 w-fit">
          <XCircle className="w-3 h-3 text-danger-text shrink-0" />
          {label}
        </Badge>
      );
    }

    if (p.status === 'draft') {
      return (
        <Badge className="text-data-xs font-bold uppercase tracking-wider bg-warning/10 text-warning-text border-warning/30 inline-flex items-center gap-1 w-fit">
          Draft
        </Badge>
      );
    }

    return (
      <Badge className="text-data-xs font-bold uppercase tracking-wider bg-primary-container text-primary border-primary/30 inline-flex items-center gap-1 w-fit">
        Proforma
      </Badge>
    );
  };

  const closedWonClients = useMemo(() => {
    if (!dealsData?.deals) return [];
    return dealsData.deals
      .filter((d) => d.stage === 'closed_won')
      .map((d) => {
        const name = d.contact_name || d.business_name || d.title;
        return { label: name, value: name };
      })
      .filter((v, i, a) => a.findIndex(t => t.value === v.value) === i);
  }, [dealsData]);

  const products = useMemo(
    () => (productData?.data ?? []) as unknown as Array<{
      _id: string; name: string; sellingPrice?: number; hsnSac?: string;
      cgstRate?: number; sgstRate?: number; igstRate?: number;
      packagingType?: string; packSize?: string; qtyPerPack?: number; totalPackSize?: string; volumeLiters?: number;
    }>,
    [productData],
  );

  const productOptions = useMemo(
    () => products.map((p) => ({ value: p._id, label: p.name })),
    [products],
  );

  const setLine = (key: string, patch: Partial<DraftLine>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const pickProduct = (key: string, productId: string) => {
    const p = products.find((x) => x._id === productId);
    setLine(key, {
      productId,
      name: p?.name || '',
      description: p?.name || '',
      unitPrice: p?.sellingPrice ?? 0,
      unit: abbreviatePackaging(derivePackagingType(p)) || '',
      packSize: p?.packSize || '',
      qtyPerPack: Number(p?.qtyPerPack) || 0,
      totalPackSize: p?.totalPackSize || '',
      volumeLiters: Number(p?.volumeLiters) || 0,
      cgstRate: p?.cgstRate ?? 0,
      sgstRate: p?.sgstRate ?? 0,
      igstRate: p?.igstRate ?? 0,
    });
  };

  const lineNet = (l: DraftLine) => {
    const raw = (l.quantity || 0) * (l.unitPrice || 0);
    const disc = l.discountType === 'percent' ? (raw * (l.discount || 0)) / 100 : l.discount || 0;
    return Math.max(0, raw - disc);
  };

  const draftSubtotal = useMemo(() => lines.reduce((s, l) => s + lineNet(l), 0), [lines]);
  const draftTax = useMemo(
    () =>
      lines.reduce((s, l) => {
        const net = lineNet(l);
        const rate = (l.cgstRate || 0) + (l.sgstRate || 0) + (l.igstRate || 0);
        return s + (net * rate) / 100;
      }, 0),
    [lines],
  );

  const resetForm = () => {
    setCustomerName('');
    setCustomerEmail('');
    setNotes('');
    setLines([emptyLine()]);
    setEditingId(null);
  };

  // Arriving from the profile page's "Edit" button (?edit=<id>) — the id
  // itself is enough to build the destination, no need to wait for it to
  // show up in whatever page of the (now paginated) list happens to be loaded.
  const editParamId = new URLSearchParams(window.location.search).get('edit');
  useEffect(() => {
    if (editParamId) navigate(`/proforma/${editParamId}/edit`, { replace: true });
  }, [editParamId, navigate]);

  const canSubmit = customerName.trim() && lines.some((l) => (l.description || l.name) && l.unitPrice > 0);

  const submit = () => {
    if (!canSubmit) return;
    const payload = {
      customerName,
      customerEmail,
      notes,
      lines: lines.map(({ key, name, ...rest }) => ({
        ...rest,
        name: name || rest.description,
      })),
    };

    if (editingId) {
      update.mutate(
        { id: editingId, payload },
        {
          onSuccess: () => { toast.success('Proforma updated.'); resetForm(); },
          onError: (err) => toast.error(errorText(err, 'Could not update the proforma.')),
        }
      );
    } else {
      create.mutate(
        payload,
        {
          onSuccess: () => { toast.success('Proforma created.'); setCreating(false); resetForm(); },
          onError: (err) => toast.error(errorText(err, 'Could not create the proforma.')),
        }
      );
    }
  };

  // CONFIRM / SEND / ACCEPT / CONVERT all live on the proforma's own detail
  // page now (SalesDocDetailPage), which is the same ladder Quotations use.
  // The list only lists.

  // The list region below renders its own skeleton while the page query is in
  // flight (aria-busy), so the header, stats and filters stay put instead of
  // the whole screen collapsing into a spinner.

  return (
    <div className="mx-auto max-w-[1580px] space-y-5 pb-16">
      <PageHeader
        icon={FileStack}
        eyebrow="Sales"
        title="Proforma Invoices"
        description="Numbered proformas for advance payment — once the client accepts, raise the tax invoice from here."
        actions={
          <Button onClick={() => navigate(ROUTES.proformaNew)}>
            <Plus className="h-4 w-4" /> New Proforma
          </Button>
        }
      />

      <StatGrid cols={3}>
        <StatCard label="Total Proformas" value={pagination?.total ?? proformas.length} hint="All proformas on record" icon={FileStack} tone="primary" />
        <StatCard label="Proforma Value" value={formatINR(totalValue)} hint="Gross total across all proformas" icon={Wallet} tone="info" />
        <StatCard
          label="Awaiting Confirmation"
          value={openCount}
          hint="Pending client acceptance"
          icon={Clock}
          tone="warning"
          progress={(pagination?.total ?? 0) > 0 ? (openCount / (pagination?.total || 1)) * 100 : undefined}
        />
      </StatGrid>

      {isFormOpen && (
        <div className="bg-card border border-border rounded-lg p-4 space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Select
              placeholder="Select Client *"
              value={customerName}
              options={closedWonClients}
              onChange={(e) => setCustomerName(e.target.value)}
            />
            <Input placeholder="Client email" value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)} />
          </div>

          <div className="space-y-2">
            {lines.map((l) => (
              <div key={l.key} className="grid gap-2 sm:grid-cols-12 items-center">
                <div className="sm:col-span-4 space-y-1">
                  <Input
                    placeholder="Item / description *"
                    value={l.description || ''}
                    onChange={(e) => setLine(l.key, { description: e.target.value })}
                  />
                  {productOptions.length > 0 && (
                    <Select
                      value={l.productId ?? ''}
                      placeholder="…or pick a catalog item"
                      options={productOptions}
                      onChange={(e) => pickProduct(l.key, e.target.value)}
                    />
                  )}
                  {packSummary(l) && (
                    <p className="text-data-xs font-semibold text-success-text">{packSummary(l)}</p>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <Input type="number" min={1} placeholder="Qty"
                    value={l.quantity}
                    onChange={(e) => setLine(l.key, { quantity: Number(e.target.value) })} />
                  {totalBaseQuantityLabel(l.quantity, l) && (
                    <p className="mt-1 text-right text-data-xs font-semibold text-success-text">
                      Total {totalBaseQuantityLabel(l.quantity, l)}
                    </p>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <Input type="number" min={0} placeholder="Rate"
                    value={l.unitPrice}
                    onChange={(e) => setLine(l.key, { unitPrice: Number(e.target.value) })} />
                  <p className="mt-1 text-right text-data-xs font-semibold text-success-text">
                    {ratePerBaseUnit(l.unitPrice, l)}
                  </p>
                </div>
                <Input className="sm:col-span-2" type="number" min={0} max={100} placeholder="Disc %"
                  value={l.discount}
                  onChange={(e) => setLine(l.key, { discount: Number(e.target.value), discountType: 'percent' })} />
                <div className="sm:col-span-1 text-right text-sm font-medium">
                  {formatINR(lineNet(l))}
                </div>
                <div className="sm:col-span-1 flex justify-end">
                  <Button variant="ghost" onClick={() => setLines((ls) =>
                    ls.length > 1 ? ls.filter((x) => x.key !== l.key) : ls)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            <Button variant="secondary" onClick={() => setLines((ls) => [...ls, emptyLine()])}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Add line
            </Button>
          </div>

          <Input placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
            <div className="text-sm">
              <span className="text-muted-foreground">Net </span>
              <strong>{formatINR(draftSubtotal)}</strong>
              <span className="text-muted-foreground"> + tax </span>
              <strong>{formatINR(draftTax)}</strong>
              <span className="text-muted-foreground"> = </span>
              <strong>{formatINR(draftSubtotal + draftTax)}</strong>
            </div>
            <Button onClick={submit} loading={create.isPending || update.isPending} disabled={!canSubmit}>
              {editingId ? 'Update proforma' : 'Save proforma'}
            </Button>
          </div>
        </div>
      )}

      {/* Search & Filter Bar */}
      <div className="bg-card rounded-card border border-border p-4 shadow-xs space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex-1 min-w-[240px] max-w-md relative">
            <Search className="w-4 h-4 text-on-surface-subtle absolute left-3 top-2.5" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by PI number or client name..."
              className="w-full pl-9 pr-4 py-2 text-xs rounded-xl border border-border bg-muted text-on-surface placeholder:text-on-surface-subtle focus-visible:ring-2 focus-visible:ring-ring focus:outline-none focus:bg-card transition"
            />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 text-xs">
              <CalendarRange className="w-3.5 h-3.5 text-on-surface-subtle" />
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="rounded-lg border border-border bg-muted px-2 py-1.5 text-xs text-on-surface-variant focus-visible:ring-2 focus-visible:ring-ring focus:outline-none focus:bg-card transition"
              />
              <span className="text-on-surface-subtle">to</span>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="rounded-lg border border-border bg-muted px-2 py-1.5 text-xs text-on-surface-variant focus-visible:ring-2 focus-visible:ring-ring focus:outline-none focus:bg-card transition"
              />
              {(fromDate || toDate) && (
                <button
                  onClick={() => { setFromDate(''); setToDate(''); }}
                  className="text-on-surface-subtle hover:text-on-surface-variant"
                  title="Clear date range"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Status Quick Filter Pills + Dropdown Filter */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="inline-flex rounded-xl border border-border bg-muted p-1 text-xs font-bold">
                <button
                  onClick={() => setStatusFilter('all')}
                  className={cn(
                    'px-2.5 py-1 rounded-lg transition',
                    statusFilter === 'all' ? 'bg-card text-primary shadow-xs' : 'text-on-surface-variant hover:text-on-surface'
                  )}
                >
                  All ({statusFilter === 'all' && pagination ? pagination.total : proformas.length})
                </button>
                <button
                  onClick={() => setStatusFilter('proforma')}
                  className={cn(
                    'px-2.5 py-1 rounded-lg transition',
                    statusFilter === 'proforma' ? 'bg-card text-primary shadow-xs' : 'text-on-surface-variant hover:text-on-surface'
                  )}
                >
                  Proforma ({openCount})
                </button>
                <button
                  onClick={() => setStatusFilter('confirmed')}
                  className={cn(
                    'px-2.5 py-1 rounded-lg transition',
                    statusFilter === 'confirmed' ? 'bg-card text-success-text shadow-xs' : 'text-on-surface-variant hover:text-on-surface'
                  )}
                >
                  Confirmed
                </button>
                <button
                  onClick={() => setStatusFilter('lost')}
                  className={cn(
                    'px-2.5 py-1 rounded-lg transition',
                    statusFilter.startsWith('lost') ? 'bg-card text-danger-text shadow-xs' : 'text-on-surface-variant hover:text-on-surface'
                  )}
                >
                  Lost
                </button>
              </div>

              {/* Only display the filter dropdown when "Lost" is selected, and only show closed lost options */}
              {statusFilter.startsWith('lost') && (
                <div className="w-56">
                  <Select
                    name="proforma-status-filter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    options={[
                      { value: 'lost', label: 'Closed Lost (All)' },
                      { value: 'lost:price_issue', label: 'Closed Lost: Price Issue' },
                      { value: 'lost:poor_quality', label: 'Closed Lost: Poor Quality' },
                      { value: 'lost:other', label: 'Closed Lost: Other Issues' },
                    ]}
                  />
                </div>
              )}
            </div>

            {/* Whole-directory roles only — a scoped role's own list is already
                restricted to themself, so this would be a dropdown of one. */}
            {canSeeWholeDirectory && (
              <div className="w-56">
                <Select
                  name="proforma-created-by-filter"
                  value={createdBy}
                  onChange={(e) => {
                    setUserSelectedFilter(true);
                    setCreatedBy(e.target.value);
                  }}
                  options={[
                    { value: '', label: 'All employees' },
                    ...(currentUser?.id ? [{ value: currentUser.id, label: `My Proformas (${currentUser.displayName || currentUser.username})` }] : []),
                    ...userOptions
                      .filter((u) => u.id !== currentUser?.id)
                      .map((u) => ({ value: u.id, label: u.display_name })),
                  ]}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="overflow-hidden rounded-card border border-border bg-card shadow-xs" aria-busy="true" aria-live="polite">
          <div className="border-b border-border bg-muted px-4 py-3">
            <Skeleton className="h-3 w-40" />
          </div>
          <div className="divide-y divide-border">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-3">
                <Skeleton className="h-3.5 w-24" />
                <Skeleton className="h-4 w-20 rounded-full" />
                <Skeleton className="h-3.5 w-48" />
                <Skeleton className="ml-auto h-3.5 w-28" />
              </div>
            ))}
          </div>
          <span className="sr-only">Loading proformas…</span>
        </div>
      ) : proformas.length === 0 ? (
        (searchInput || fromDate || toDate || statusFilter !== 'all') ? (
          <EmptyState
            title="No proformas match these filters"
            description="Nothing was found for the current search and filters. Clear them to see the full list."
            action={
              <Button variant="outline" onClick={() => { setSearchInput(''); setStatusFilter('all'); setFromDate(''); setToDate(''); }}>
                Clear filters
              </Button>
            }
          />
        ) : (
          <EmptyState
            title="No proforma invoices yet"
            description="Raise one from a confirmed quotation when the client wants terms in writing before the tax invoice."
            action={<Button onClick={() => navigate(ROUTES.proformaNew)}>New Proforma</Button>}
          />
        )
      ) : (
        /* ── THE SAME TABLE QUOTATIONS USE ──
            This list was a stack of cards carrying its own Confirm, Edit,
            Convert and Create-Tax-Invoice buttons, none of which the Quotations
            list has — so two documents with one identical lifecycle were
            operated in two different places, and the row buttons fired one
            request per row just to work out which of them to draw. The row is
            now what a list row should be: identity, money, date, and a way to
            look at the document. Everything that CHANGES a proforma lives on
            its detail page, which already has the whole ladder (Confirm →
            Send → Mark as Accepted → Convert → Create Tax Invoice) and is one
            click away. */
        <div className="rounded-card border border-border bg-card shadow-xs overflow-hidden">
          <div className={cn('overflow-x-auto transition-opacity', isFetching && 'opacity-60')}>
            <table className="w-full text-left text-body-sm min-w-[860px]">
              <thead>
                <tr className="border-b border-border text-label-sm uppercase tracking-wider text-on-surface-variant">
                  <th scope="col" className="sticky top-0 z-10 bg-muted py-3 pl-4 pr-3">PI Number</th>
                  <th scope="col" className="sticky top-0 z-10 bg-muted py-3 pr-3">Status</th>
                  <th
                    scope="col"
                    className="sticky top-0 z-10 cursor-pointer select-none bg-muted py-3 pr-3 hover:text-on-surface"
                    onClick={() => toggleSort('customer')}
                  >
                    <span className="inline-flex items-center gap-1">Client Name {sortIcon('customer')}</span>
                  </th>
                  <th scope="col" className="sticky top-0 z-10 bg-muted py-3 pr-3">Phone</th>
                  <th scope="col" className="sticky top-0 z-10 bg-muted py-3 pr-3">Email</th>
                  <th
                    scope="col"
                    className="sticky top-0 z-10 cursor-pointer select-none bg-muted py-3 pr-3 text-right hover:text-on-surface"
                    onClick={() => toggleSort('amount')}
                  >
                    <span className="inline-flex items-center justify-end gap-1">Amount {sortIcon('amount')}</span>
                  </th>
                  <th
                    scope="col"
                    className="sticky top-0 z-10 cursor-pointer select-none bg-muted py-3 pr-3 hover:text-on-surface"
                    onClick={() => toggleSort('date')}
                  >
                    <span className="inline-flex items-center gap-1">Date {sortIcon('date')}</span>
                  </th>
                  <th scope="col" className="sticky top-0 z-10 bg-muted py-3 pr-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {proformas.map((p) => {
                  const piNumber = p.orderNumber || p.documentNumber || p._id.slice(-6);
                  return (
                    <tr
                      key={p._id}
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate(`/proforma/${p._id}`)}
                      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate(`/proforma/${p._id}`)}
                      className="border-b border-border last:border-0 cursor-pointer hover:bg-primary-container/40 transition-colors"
                    >
                      <td className="py-3 pl-4 pr-3 whitespace-nowrap">
                        <span className="font-extrabold text-on-surface">{piNumber}</span>
                        {p.customerPoNumber && (
                          <div className="text-data-xs font-semibold text-success-text bg-success/10 px-1.5 py-0.5 rounded border border-success/30 mt-0.5 inline-block">
                            PO: {p.customerPoNumber}
                          </div>
                        )}
                      </td>
                      <td className="py-3 pr-3 whitespace-nowrap">
                        {renderStatusBadge(p)}
                      </td>
                      <td className="py-3 pr-3">
                        <span className="font-bold text-on-surface flex items-center gap-1">
                          <FileStack className="w-3.5 h-3.5 text-on-surface-subtle shrink-0" />
                          {p.customerName || 'No Client Name'}
                        </span>
                      </td>
                      <td className="py-3 pr-3 text-on-surface-variant whitespace-nowrap">{p.customerPhone || '—'}</td>
                      <td className="py-3 pr-3 text-on-surface-variant">{p.customerEmail || '—'}</td>
                      <td className="whitespace-nowrap py-3 pr-3 text-right font-mono tabular-nums text-data-md font-semibold text-on-surface">
                        {formatDocTotal(p as never)}
                        {docInrEquivalent(p as never) && <div className="text-data-xs font-medium text-on-surface-subtle">≈ {docInrEquivalent(p as never)}</div>}
                      </td>
                      <td className="py-3 pr-3 text-on-surface-variant whitespace-nowrap">
                        {new Date(p.date).toLocaleDateString('en-IN')}
                      </td>
                      <td className="py-3 pr-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1.5">
                          {!['cancelled', 'expired', 'superseded'].includes(p.status) && (
                            <Button size="sm" variant="secondary" onClick={() => navigate(`/proforma/${p._id}/edit`)}>Edit</Button>
                          )}
                          <button
                            type="button"
                            onClick={() => setPreviewProforma(p)}
                            title="View document"
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-primary/30 text-primary hover:bg-primary-container transition shrink-0"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          <a
                            href={`/api/documents/proforma/${p._id}?format=pdf&download=1`}
                            target="_blank"
                            rel="noreferrer"
                            title="Download PDF"
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-on-surface-variant hover:bg-muted transition shrink-0"
                          >
                            <Download className="w-3.5 h-3.5" />
                          </a>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination footer — the table only ever holds one page's worth
              of rows in the DOM, however large the real proforma history is. */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-muted px-4 py-3">
              <span className="text-data-xs font-semibold text-on-surface-variant">
                Showing {(pagination.page - 1) * pagination.limit + 1}
                –{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
              </span>
              <div className="flex items-center gap-2">
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="rounded-lg border border-border bg-card px-2 py-1 text-data-xs font-semibold text-on-surface-variant"
                >
                  {[25, 50, 100, 200].map((n) => (
                    <option key={n} value={n}>{n} / page</option>
                  ))}
                </select>
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={pagination.page <= 1}
                  className="flex h-7 w-7 items-center justify-center rounded-lg border border-border bg-card text-on-surface-variant disabled:opacity-40 hover:bg-muted transition"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <span className="text-data-xs font-bold text-on-surface-variant min-w-[70px] text-center">
                  Page {pagination.page} / {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={pagination.page >= pagination.totalPages}
                  className="flex h-7 w-7 items-center justify-center rounded-lg border border-border bg-card text-on-surface-variant disabled:opacity-40 hover:bg-muted transition"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* INTERACTIVE IN-APP PROFORMA PREVIEW MODAL */}
      {previewProforma && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-inverse-surface/60 backdrop-blur-xs p-3 md:p-6 animate-in fade-in duration-200">
          <div className="bg-card rounded-card border border-border shadow-2xl max-w-4xl w-full h-[90vh] flex flex-col overflow-hidden">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-border bg-muted">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-info/10 text-info-text flex items-center justify-center font-black">
                  <FileStack className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm font-extrabold text-on-surface">
                    Proforma Document: {previewProforma.orderNumber || previewProforma.documentNumber || previewProforma._id.slice(-6)}
                  </h3>
                  <p className="text-data-xs text-on-surface-variant font-medium">
                    {previewProforma.customerName} · Total: {formatDocTotal(previewProforma as never)}
                  </p>
                </div>
              </div>

              {/* Action Toolbar */}
              <div className="flex items-center gap-2">
                <a
                  href={`/api/documents/proforma/${previewProforma._id}?format=pdf&download=1`}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-1.5 rounded-xl bg-primary hover:bg-primary-dim text-white text-xs font-bold flex items-center gap-1.5 shadow-xs transition"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Download PDF</span>
                </a>

                <a
                  href={`/api/documents/proforma/${previewProforma._id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-1.5 rounded-xl border border-border bg-card text-on-surface-variant hover:bg-muted text-xs font-semibold flex items-center gap-1.5 shadow-2xs transition"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  <span>Open in Tab</span>
                </a>

                <button
                  onClick={() => setPreviewProforma(null)}
                  className="p-1.5 rounded-xl text-on-surface-subtle hover:text-on-surface-variant hover:bg-muted transition ml-2"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Embedded Iframe Document Preview */}
            <div className="flex-1 bg-muted p-2 md:p-4 overflow-hidden">
              <iframe
                src={`/api/documents/proforma/${previewProforma._id}`}
                className="w-full h-full rounded-xl bg-card border border-border shadow-inner"
                title={`Proforma Preview ${previewProforma._id}`}
              />
            </div>
          </div>
        </div>
      )}

      {selectedForLostModal && (
        <LostReasonModal
          deal={{ title: selectedForLostModal.orderNumber || selectedForLostModal.documentNumber || selectedForLostModal.customerName || 'Proforma' } as any}
          onCancel={() => setSelectedForLostModal(null)}
          onConfirm={async (payload) => {
            try {
              await markLost.mutateAsync({
                id: selectedForLostModal._id,
                payload: {
                  ...payload,
                  closeDeal: payload.closeDeal,
                },
              });
              toast.success('Proforma marked as Closed Lost');
              setSelectedForLostModal(null);
            } catch (err) {
              toast.error(errorText(err, 'Failed to mark proforma as lost'));
            }
          }}
          isSaving={markLost.isPending}
          closeDealChoice={
            selectedForLostModal.crmDealId || selectedForLostModal.crmLeadId
              ? { dealTitle: selectedForLostModal.customerName || 'the linked deal' }
              : undefined
          }
        />
      )}

    </div>
  );
}
